/*
 * Numbers for the personal Results and Portfolio Simulator pages. A "source"
 * is anything with a daily value curve: a connected account or one of the
 * user's own strategies. points: [{ date: 'YYYY-MM-DD', value, netFlow }].
 */

export const PERIODS = [
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: 'YTD', label: 'YTD' },
  { value: '1Y', label: '1Y' },
  { value: 'All', label: 'All' },
];

/** First date in a period ('All' → null). */
export function periodStart(period, today = new Date()) {
  const d = new Date(today);
  if (period === 'All') return null;
  if (period === 'YTD') return `${d.getFullYear()}-01-01`;
  const months = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 }[period] || 3;
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

export function inPeriod(points, period) {
  const start = periodStart(period);
  if (!start) return points;
  return points.filter((p) => p.date >= start);
}

/** Day-by-day P&L: change in value minus money moved in or out. */
export function dailyPnl(points) {
  const out = [];
  for (let i = 1; i < points.length; i += 1) {
    const pnl = (Number(points[i].value) || 0) - (Number(points[i - 1].value) || 0) - (Number(points[i].netFlow) || 0);
    out.push({ date: points[i].date, pnl: Math.round(pnl * 100) / 100 });
  }
  return out;
}

/** Worst peak-to-trough fall, in dollars and as a % of the peak. */
export function maxDrawdown(points) {
  let peak = -Infinity;
  let worst = 0;
  let worstPct = 0;
  points.forEach((p) => {
    const v = Number(p.value) || 0;
    peak = Math.max(peak, v);
    const dd = v - peak;
    if (dd < worst) worst = dd;
    if (peak > 0 && dd / peak < worstPct) worstPct = dd / peak;
  });
  return { amount: worst, pct: worstPct };
}

/** Drawdown at each date (for charts): % below the running peak. */
export function drawdownSeries(points) {
  let peak = -Infinity;
  return points.map((p) => {
    const v = Number(p.value) || 0;
    peak = Math.max(peak, v);
    return { date: p.date, dd: peak > 0 ? (v - peak) / peak : 0 };
  });
}

/** Return per calendar month: P&L in the month over the value at its start. */
export function monthlyReturns(points) {
  const out = new Map();
  for (let i = 1; i < points.length; i += 1) {
    const key = points[i].date.slice(0, 7);
    const pnl = (Number(points[i].value) || 0) - (Number(points[i - 1].value) || 0) - (Number(points[i].netFlow) || 0);
    const row = out.get(key) || { month: key, pnl: 0, base: Number(points[i - 1].value) || 0 };
    row.pnl += pnl;
    out.set(key, row);
  }
  return [...out.values()].map((r) => ({ month: r.month, pnl: r.pnl, ret: r.base > 0 ? r.pnl / r.base : null }));
}

/** Headline stats for one source over its points. */
export function sourceStats(points) {
  const daily = dailyPnl(points);
  const wins = daily.filter((d) => d.pnl > 0);
  const losses = daily.filter((d) => d.pnl < 0);
  const start = points.length ? Number(points[0].value) || 0 : 0;
  const end = points.length ? Number(points[points.length - 1].value) || 0 : 0;
  const net = daily.reduce((s, d) => s + d.pnl, 0);
  const grossWin = wins.reduce((s, d) => s + d.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((s, d) => s + d.pnl, 0));
  const dd = maxDrawdown(points);
  return {
    start,
    end,
    net,
    ret: start > 0 ? net / start : null,
    maxDd: dd.amount,
    maxDdPct: dd.pct,
    winRate: wins.length + losses.length ? wins.length / (wins.length + losses.length) : null,
    winDays: wins.length,
    lossDays: losses.length,
    profitFactor: grossLoss ? grossWin / grossLoss : (grossWin ? Infinity : null),
    best: daily.length ? Math.max(...daily.map((d) => d.pnl)) : 0,
    worst: daily.length ? Math.min(...daily.map((d) => d.pnl)) : 0,
    days: daily.length,
    firstDate: points[0]?.date || null,
    lastDate: points[points.length - 1]?.date || null,
  };
}

/** Pearson correlation of two daily P&L series on the dates both have. */
export function correlation(a, b) {
  const mb = new Map(b.map((d) => [d.date, d.pnl]));
  const pairs = a.filter((d) => mb.has(d.date)).map((d) => [d.pnl, mb.get(d.date)]);
  if (pairs.length < 5) return null;
  const n = pairs.length;
  const ma = pairs.reduce((s, p) => s + p[0], 0) / n;
  const mbv = pairs.reduce((s, p) => s + p[1], 0) / n;
  let num = 0;
  let da = 0;
  let db = 0;
  pairs.forEach(([x, y]) => { num += (x - ma) * (y - mbv); da += (x - ma) ** 2; db += (y - mbv) ** 2; });
  if (!da || !db) return null;
  return num / Math.sqrt(da * db);
}

/**
 * Combine sources: each source's daily P&L times its weight, summed by date,
 * on top of a starting balance. Returns the combined curve and daily P&L.
 */
export function combine(sources, weights, startingCapital) {
  const byDate = new Map();
  sources.forEach((s) => {
    const w = Number(weights[s.key] ?? 1);
    if (!w) return;
    dailyPnl(s.points).forEach((d) => byDate.set(d.date, (byDate.get(d.date) || 0) + d.pnl * w));
  });
  const dates = [...byDate.keys()].sort();
  let value = Number(startingCapital) || 0;
  const daily = dates.map((date) => ({ date, pnl: Math.round(byDate.get(date) * 100) / 100 }));
  const points = [];
  if (dates.length) {
    const d0 = new Date(`${dates[0]}T12:00:00Z`);
    d0.setUTCDate(d0.getUTCDate() - 1);
    points.push({ date: d0.toISOString().slice(0, 10), value, netFlow: 0 });
  }
  daily.forEach((d) => { value += d.pnl; points.push({ date: d.date, value: Math.round(value * 100) / 100, netFlow: 0 }); });
  return { points, daily };
}

export const PROP_PRESETS = [
  { id: 'apex50', label: 'Apex 50K', size: 50000, trailing: 2500, dailyLoss: null, target: 3000, stopTrailAtStart: true },
  { id: 'apex100', label: 'Apex 100K', size: 100000, trailing: 3000, dailyLoss: null, target: 6000, stopTrailAtStart: true },
  { id: 'topstep50', label: 'Topstep 50K', size: 50000, trailing: 2000, dailyLoss: 1000, target: 3000, stopTrailAtStart: true },
  { id: 'custom', label: 'Custom', size: 50000, trailing: 2500, dailyLoss: null, target: 3000, stopTrailAtStart: true },
];

/**
 * Replay daily P&L against a prop firm's rules: a trailing drawdown from the
 * highest balance (which stops trailing once it reaches the starting balance,
 * as Apex and Topstep do), an optional daily loss limit, and a profit target.
 */
export function propCheck(daily, { size, trailing, dailyLoss, target, stopTrailAtStart = true }) {
  let balance = size;
  let peak = size;
  let floor = size - trailing;
  let tightest = trailing;
  const path = [];
  for (const d of daily) {
    balance += d.pnl;
    peak = Math.max(peak, balance);
    floor = Math.max(floor, Math.min(peak - trailing, stopTrailAtStart ? size : Infinity));
    tightest = Math.min(tightest, balance - floor);
    path.push({ date: d.date, balance, floor });
    if (dailyLoss && d.pnl <= -dailyLoss) {
      return { outcome: 'blown', reason: 'daily-loss', date: d.date, balance, floor, dayPnl: d.pnl, path, tightest };
    }
    if (balance <= floor) {
      return { outcome: 'blown', reason: 'drawdown', date: d.date, balance, floor, path, tightest };
    }
    if (target && balance - size >= target) {
      return { outcome: 'passed', date: d.date, balance, floor, path, tightest };
    }
  }
  return { outcome: 'running', balance, floor, path, tightest, toTarget: target ? size + target - balance : null, cushion: balance - floor };
}

/**
 * A source's daily P&L as weekly rows in the Results sheet layout the
 * Portfolio Simulator reads: [Monday 'MM.DD.YYYY', '', Mon, Tue, Wed, Thu, Fri, week total].
 * Weekend P&L (crypto) is counted on that week's Friday so week totals stay right.
 */
export const WEEKLY_SHEET_HEADER = [['Week', '', '', '', '', '', '', ''], ['Week of', '', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Total']];

export function toWeeklySheet(points) {
  const weeks = new Map();
  dailyPnl(points).forEach(({ date, pnl }) => {
    if (!pnl) return;
    const d = new Date(`${date}T12:00:00Z`);
    const dow = d.getUTCDay(); // 0 Sun … 6 Sat
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - ((dow + 6) % 7));
    const key = `${String(monday.getUTCMonth() + 1).padStart(2, '0')}.${String(monday.getUTCDate()).padStart(2, '0')}.${monday.getUTCFullYear()}`;
    const row = weeks.get(key) || [key, '', 0, 0, 0, 0, 0, 0];
    const col = dow >= 1 && dow <= 5 ? dow + 1 : 6;
    row[col] = Math.round((row[col] + pnl) * 100) / 100;
    row[7] = Math.round((row[7] + pnl) * 100) / 100;
    weeks.set(key, row);
  });
  const rows = [...weeks.values()].sort((a, b) => {
    const k = (s) => { const [m, dd, y] = s.split('.'); return `${y}${m}${dd}`; };
    return k(b[0]).localeCompare(k(a[0]));
  });
  return [...WEEKLY_SHEET_HEADER.map((r) => [...r]), ...rows];
}
